import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Star, Plus, Minus, ShoppingCart } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { useAuth } from '../../contexts/AuthContext';
import './ProductCard.css';

const ProductCard = ({
  id,
  name,
  brand,
  condition,
  price,
  originalPrice,
  rating = 0,
  reviewsCount = 0,
  image,
  model,
  qty_instock = 0,
  location = 'Centerville'
}) => {
  const navigate = useNavigate();
  const { cartItems, addToCart, updateQuantity, removeItem } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { currentUser } = useAuth();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const parsedPrice = Number(price);
  const parsedOriginalPrice = Number(originalPrice);
  const hasValidPrice = Number.isFinite(parsedPrice);
  const hasValidOriginalPrice = Number.isFinite(parsedOriginalPrice) && parsedOriginalPrice > 0;
  const discount = hasValidPrice && hasValidOriginalPrice && parsedOriginalPrice > parsedPrice
    ? Math.round((1 - parsedPrice / parsedOriginalPrice) * 100)
    : null;

  // Get quantity from cart items
  const cartItem = cartItems?.find(item => item.id === id) || null;
  const quantity = cartItem ? cartItem.quantity : 0;

  useEffect(() => {
    const checkWishlistStatus = async () => {
      if (currentUser) {
        try {
          const status = await isInWishlist(id);
          setIsWishlisted(status);
        } catch (error) {
          console.error('Error checking wishlist status:', error);
        }
      }
    };
    checkWishlistStatus();
  }, [currentUser, id, isInWishlist]);

  const handleCardClick = (e) => {
    // Prevent navigation if clicking on buttons
    if (e.target.closest('button')) {
      e.stopPropagation();
      return;
    }
    navigate(`/product/${id}`);
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      if (isWishlisted) {
        await removeFromWishlist(id);
        setIsWishlisted(false);
      } else {
        await addToWishlist({
          id,
          name,
          price,
          originalPrice,
          image,
          qty_instock
        });
        setIsWishlisted(true);
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
    }
  };

  const handleQuantityChange = (change) => {
    setSelectedQuantity(prev => Math.max(1, Math.min(qty_instock, prev + change)));
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart({
      id,
      name,
      price,
      image,
      qty_instock
    }, selectedQuantity);
  };

  const handleRemoveFromCart = (e) => {
    e.stopPropagation();
    if (quantity === 1) {
      removeItem(id);
    } else {
      updateQuantity(id, -1);
    }
  };

  return (
    <div className="product-card" onClick={handleCardClick}>
      <button 
        className={`favorite-button ${isWishlisted ? 'active' : ''}`}
        onClick={handleFavoriteClick}
        type="button"
        aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <Heart color='black' fill={isWishlisted ? "#ff4081" : "none"} />
      </button>

      <div className="product-image-container">
        <img src={image} alt={name} className="product-image" />
      </div>

      <div className="product-info">
        <div className="product-eyebrow">
          {brand && <span className="product-brand">{brand}</span>}
          {condition && <span className="product-condition">{condition}</span>}
        </div>
        <h3>{name}</h3>
        {model && <p className="product-model">{model}</p>}
        <div className="rating">
          <Star className="star-icon" size={16} />
          <span>{rating}</span>
          {reviewsCount > 0 && <span>({reviewsCount})</span>}
        </div>
        <div className="price-info">
          <span className="price">
            {hasValidPrice ? `$${parsedPrice.toFixed(2)}` : 'Price unavailable'}
          </span>
          {hasValidOriginalPrice && (
            <span className="original-price">${parsedOriginalPrice.toFixed(2)}</span>
          )}
          {discount !== null && (
            <span className="discount-badge">{discount}% OFF</span>
          )}
        </div>
        {qty_instock > 0 ? (
          <span className="stock-status in-stock">{qty_instock} in stock</span>
        ) : (
          <span className="stock-status out-of-stock">Out of stock</span>
        )}
      </div>

      <div className="product-actions">
        <div className="quantity-controls">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleQuantityChange(-1);
            }}
            type="button"
            aria-label="Decrease quantity"
            disabled={selectedQuantity <= 1}
          >
            <Minus size={16} />
          </button>
          <span>{selectedQuantity}</span>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleQuantityChange(1);
            }}
            type="button"
            aria-label="Increase quantity"
            disabled={selectedQuantity >= qty_instock}
          >
            <Plus size={16} />
          </button>
        </div>
        <button 
          className="add-to-cart" 
          onClick={handleAddToCart}
          disabled={qty_instock === 0}
          type="button"
        >
          <ShoppingCart size={16} />
          Add to Cart
        </button>
        {cartItem && (
          <div className="quantity-controls">
            <button 
              onClick={handleRemoveFromCart}
              type="button"
              aria-label="Decrease quantity"
            >
              <Minus size={16} />
            </button>
            <span>{quantity}</span>
            <button 
              onClick={handleAddToCart}
              disabled={quantity >= qty_instock}
              type="button"
              aria-label="Increase quantity"
            >
              <Plus size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
